import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowsRightLeftIcon, BoltIcon, CheckCircleIcon, ExclamationTriangleIcon, ArrowPathIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { api } from '../services/api';
import { LineItem, POLinesForMatchingResponse } from '../types/invoice';
import { autoMatchPoLines } from '../utils/poAutoMatch';

interface Props {
  poNumber: string;
  vendorNumber?: string;
  invoiceLines: LineItem[];
  onMatch: (
    invoiceLineIndex: number,
    poDetailKey: string,
    poDefaults?: {
      quantity?: number | null;
      unit_price?: number | null;
      subject_to_freight?: boolean | null;
      subject_to_discount?: boolean | null;
      // The PO line's GL account. Parent backfills the invoice line's gl_id
      // when blank so the field visibly matches the "GL IDs: Set by PO" pill,
      // instead of staying empty and looking like the match did nothing.
      gl_account_number?: string | null;
    },
  ) => void;
  /** When provided, renders inside a full-screen modal with a close button
   *  that invokes this handler. When omitted, renders inline. */
  onClose?: () => void;
}

const fmtMoney = (n: number | null | undefined) =>
  n == null ? '—' : `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtQty = (n: number | null | undefined) =>
  n == null ? '—' : n.toLocaleString(undefined, { maximumFractionDigits: 2 });

/**
 * Two-pane view for matching an invoice's line items to a PO's line items.
 * Left pane: invoice lines with a per-line dropdown to pick a PO line.
 * Right pane: PO lines fetched from Keystone, showing the unique
 * `detail_key` (Redback @ID) that ends up in the export column so the
 * import charges the correct PO detail record instead of relying on
 * positional order (which vendors do not preserve).
 *
 * Match state persists on each invoice line's `matched_po_detail_key`;
 * the parent owns the invoice data and receives updates via `onMatch`.
 */
const POLineMatcher: React.FC<Props> = ({ poNumber, vendorNumber, invoiceLines, onMatch, onClose }) => {
  const [poData, setPoData] = useState<POLinesForMatchingResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Count of pairings produced by the last auto-match run. Resets to 0 on
  // every manual click so the clerk sees fresh counts after reruns.
  const [autoMatchCount, setAutoMatchCount] = useState(0);
  // Guard so the on-load auto-match fires at most once per matcher mount.
  const didAutoRunRef = useRef(false);

  const fetchPO = useCallback(async () => {
    if (!poNumber?.trim()) {
      setPoData(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string> = {};
      if (vendorNumber) params.vendor_number = vendorNumber;
      const res = await api.get<POLinesForMatchingResponse>(
        `/po/${encodeURIComponent(poNumber.trim())}/lines-for-matching`,
        { params },
      );
      setPoData(res.data);
    } catch (e: any) {
      const serverDetail = e?.response?.data?.detail;
      const msg =
        serverDetail
          ? String(serverDetail)
          : e?.response?.status === 404
            ? `PO "${poNumber}" not found.`
            : e?.message || 'Failed to load PO.';
      setError(String(msg));
      setPoData(null);
    } finally {
      setLoading(false);
    }
  }, [poNumber, vendorNumber]);

  useEffect(() => {
    fetchPO();
  }, [fetchPO]);

  // Shared matcher — called both from the on-load auto-run and from the
  // "Auto-match remaining" button. Pushes each new pairing through the
  // parent's onMatch so matched_po_detail_key + any backfilled defaults
  // (unit_price, gl_account_number, subject_to_* flags) land on the
  // invoice line exactly like a manual pick does.
  const runAutoMatch = useCallback(() => {
    if (!poData) return 0;
    const matches = autoMatchPoLines(invoiceLines, poData.lines);
    matches.forEach(({ invoiceIndex, poDetailKey, poLine }) => {
      onMatch(invoiceIndex, poDetailKey, {
        quantity: poLine.quantity,
        unit_price: poLine.unit_price,
        subject_to_freight: poLine.subject_to_freight,
        subject_to_discount: poLine.subject_to_discount,
        gl_account_number: poLine.gl_account_number,
      });
    });
    setAutoMatchCount(matches.length);
    return matches.length;
  }, [poData, invoiceLines, onMatch]);

  // When the PO data first loads and nothing is matched yet, run the
  // auto-matcher silently so clerks don't have to click to see the
  // obvious pairings. Only once per matcher mount, so re-renders from
  // the parent (e.g. the clerk editing a line) do not clobber manual
  // tweaks the clerk made after the initial auto-match.
  useEffect(() => {
    if (didAutoRunRef.current) return;
    if (!poData || !poData.lines || poData.lines.length === 0) return;
    if (invoiceLines.length === 0) return;
    const alreadyMatched = invoiceLines.some((l) => (l.matched_po_detail_key || '').trim());
    if (alreadyMatched) {
      didAutoRunRef.current = true;
      return;
    }
    didAutoRunRef.current = true;
    runAutoMatch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poData]);

  // Which invoice line indexes are currently matched to each PO detail_key
  const matchesByPoKey = useMemo(() => {
    const map: Record<string, number[]> = {};
    invoiceLines.forEach((line, idx) => {
      const key = line.matched_po_detail_key?.trim();
      if (key) {
        (map[key] = map[key] || []).push(idx);
      }
    });
    return map;
  }, [invoiceLines]);

  // Only count a line as matched when its matched_po_detail_key resolves
  // to a row on the CURRENTLY-loaded PO. A stale key left over from a
  // previously-matched PO (clerk retyped the PO Number) would otherwise
  // show as "matched" even though the dropdown says "unmatched" because
  // the key does not belong to any line on the new PO.
  const currentPoKeys = useMemo(() => {
    const s = new Set<string>();
    if (poData?.lines) {
      poData.lines.forEach((p) => {
        if (p.detail_key) s.add(p.detail_key);
      });
    }
    return s;
  }, [poData]);
  const matchedCount = invoiceLines.filter((l) => {
    const key = (l.matched_po_detail_key || '').trim();
    return !!key && currentPoKeys.has(key);
  }).length;
  const totalLines = invoiceLines.length;
  const allMatched = totalLines > 0 && matchedCount === totalLines;

  const content = (
    <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden bg-white dark:bg-gray-800">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-brand-50 dark:bg-brand-900/20 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <ArrowsRightLeftIcon className="h-5 w-5 text-brand-600 dark:text-brand-400" />
          <div>
            <div className="text-sm font-semibold text-gray-900 dark:text-white">
              PO Line Matching
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 break-all">
              {poData?.vendor_name ? `${poData.vendor_name} · ` : ''}PO <span className="font-mono">{poNumber}</span>
              {poData && ` · ${poData.matchable_lines} PO line${poData.matchable_lines === 1 ? '' : 's'}`}
            </div>
            {poData && (poData.purchase_order_date || poData.fiscal_year || poData.po_total != null) && (
              <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5 flex items-center gap-2 flex-wrap">
                {poData.po_total != null && (
                  <span>PO Total <span className="font-semibold text-gray-700 dark:text-gray-300">{fmtMoney(poData.po_total)}</span></span>
                )}
                {poData.purchase_order_date && <span>· Dated {poData.purchase_order_date}</span>}
                {poData.fiscal_year && <span>· FY {poData.fiscal_year}</span>}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className={`text-xs font-medium ${allMatched ? 'text-green-700 dark:text-green-400' : matchedCount > 0 ? 'text-yellow-700 dark:text-yellow-400' : 'text-gray-500 dark:text-gray-400'}`}>
            {matchedCount} of {totalLines} matched
            {autoMatchCount > 0 && (
              <span className="ml-1 text-gray-500 dark:text-gray-400 font-normal">
                ({autoMatchCount} auto)
              </span>
            )}
          </span>
          <button
            type="button"
            onClick={runAutoMatch}
            disabled={loading || !poData || !poData.lines || poData.lines.length === 0 || allMatched}
            title="Score every unmatched invoice line against every unmatched PO line and apply pairings scoring at least 70% confidence."
            className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium border border-brand-600 text-brand-600 dark:text-brand-400 dark:border-brand-500 hover:bg-brand-50 dark:hover:bg-brand-900/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <BoltIcon className="h-3.5 w-3.5" />
            Auto-match remaining
          </button>
          <button
            type="button"
            onClick={fetchPO}
            disabled={loading}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            title="Refresh PO"
          >
            <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              title="Close"
              aria-label="Close"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>

      {/* Error state */}
      {error && (
        <div className="p-4 flex items-start gap-2 bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800">
          <ExclamationTriangleIcon className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-red-700 dark:text-red-300">{error}</div>
        </div>
      )}

      {/* Warn when the PO's actual vendor differs from the invoice's vendor.
          We surface the data anyway (hiding it would block correct matches
          when OCR mis-extracts the vendor) but flag the mismatch. */}
      {poData?.vendor_mismatch && (
        <div className="p-3 flex items-start gap-2 bg-yellow-50 dark:bg-yellow-900/20 border-b border-yellow-200 dark:border-yellow-800">
          <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-yellow-800 dark:text-yellow-200">
            Vendor mismatch: PO belongs to vendor <strong>{poData.vendor_number}</strong>
            {poData.vendor_name ? ` (${poData.vendor_name})` : ''}, but this invoice's
            vendor is <strong>{poData.invoice_vendor_number}</strong>.
            Double-check before matching — OCR may have picked up the wrong vendor.
          </div>
        </div>
      )}

      {/* Two-pane body */}
      <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-gray-200 dark:divide-gray-700">
        {/* LEFT: Invoice lines */}
        <div>
          <div className="px-4 py-2 bg-gray-50 dark:bg-gray-700/50 border-b border-gray-100 dark:border-gray-700">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Invoice lines
            </span>
          </div>
          {invoiceLines.length === 0 && (
            <div className="px-4 py-6 text-sm text-gray-500 dark:text-gray-400 text-center">
              No line items on this invoice.
            </div>
          )}
          {invoiceLines.map((line, idx) => {
            const matched = line.matched_po_detail_key?.trim() || '';
            const matchedPoLine = poData?.lines.find((p) => p.detail_key === matched);
            return (
              <div
                key={`inv-${idx}`}
                className={`p-3 border-b border-gray-100 dark:border-gray-700 last:border-b-0 ${
                  matched ? 'bg-green-50/40 dark:bg-green-900/10' : ''
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-400 font-mono">#{idx + 1}</span>
                      <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {line.description || <em className="text-gray-400">no description</em>}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      Qty {fmtQty(line.quantity)} · Unit {fmtMoney(line.unit_price)} · Total {fmtMoney(line.total_price)}
                    </div>
                  </div>
                  {matched && (
                    <CheckCircleIcon className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                  )}
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <label className="text-[11px] font-medium text-gray-500 dark:text-gray-400 flex-shrink-0">
                    Match to PO line:
                  </label>
                  <select
                    value={matched}
                    onChange={(e) => {
                      const detailKey = e.target.value;
                      const picked = poData?.lines.find((p) => p.detail_key === detailKey);
                      onMatch(
                        idx,
                        detailKey,
                        picked
                          ? {
                              quantity: picked.quantity,
                              unit_price: picked.unit_price,
                              subject_to_freight: picked.subject_to_freight,
                              subject_to_discount: picked.subject_to_discount,
                              gl_account_number: picked.gl_account_number,
                            }
                          : undefined,
                      );
                    }}
                    disabled={!poData || loading}
                    className="flex-1 min-w-0 px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded-md
                      bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                      focus:ring-2 focus:ring-brand-500 focus:border-brand-500 disabled:opacity-50"
                  >
                    <option value="">— unmatched —</option>
                    {poData?.lines.map((po) => (
                      <option key={po.detail_key} value={po.detail_key}>
                        #{po.line_number} · {(po.description || '(no desc)').slice(0, 40)} · {fmtMoney(po.unit_price)} · key {po.detail_key}
                      </option>
                    ))}
                  </select>
                </div>
                {matchedPoLine && (
                  <div className="mt-1.5 text-[11px] text-gray-500 dark:text-gray-400 font-mono">
                    → detail_key <span className="text-green-700 dark:text-green-400 font-semibold">{matchedPoLine.detail_key}</span> · GL {matchedPoLine.gl_account_number}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* RIGHT: PO lines */}
        <div className="bg-gray-50/50 dark:bg-gray-900/30">
          <div className="px-4 py-2 bg-gray-50 dark:bg-gray-700/50 border-b border-gray-100 dark:border-gray-700">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              PO lines
            </span>
          </div>
          {loading && !poData && (
            <div className="px-4 py-6 text-sm text-gray-500 dark:text-gray-400 text-center">
              Loading PO {poNumber}…
            </div>
          )}
          {poData?.lines.length === 0 && (
            <div className="px-4 py-6 text-sm text-gray-500 dark:text-gray-400 text-center">
              This PO has no matchable (open) lines.
            </div>
          )}
          <div className="p-2 space-y-2">
            {poData?.lines.map((po) => {
              const matchedTo = matchesByPoKey[po.detail_key] || [];
              const isMatched = matchedTo.length > 0;
              const isReused = matchedTo.length > 1; // many-to-one blanket-PO signal
              const enc = po.amount_open_encumbered;
              const encEmpty = enc != null && enc <= 0;
              return (
                <div
                  key={`po-${po.detail_key}`}
                  className={`rounded-lg border shadow-sm transition-colors ${
                    isReused
                      ? 'border-blue-300 dark:border-blue-700 bg-blue-50/60 dark:bg-blue-900/10'
                      : isMatched
                        ? 'border-green-300 dark:border-green-700 bg-green-50/60 dark:bg-green-900/10'
                        : encEmpty
                          ? 'border-gray-200 dark:border-gray-700 bg-white/50 dark:bg-gray-800/40 opacity-60'
                          : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
                  }`}
                >
                  <div className="p-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline gap-2">
                          <span className="text-xs text-gray-400 font-mono flex-shrink-0">#{po.line_number}</span>
                          <span className="text-sm font-semibold text-gray-900 dark:text-white break-words">
                            {po.description || <em className="text-gray-400 font-normal">no description</em>}
                          </span>
                        </div>
                      </div>
                      <div className="flex-shrink-0 flex flex-col items-end gap-1">
                        {po.status_label && po.status_label !== 'open' && (
                          <span
                            className={`text-[10px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wide ${
                              po.status_label === 'completed'
                                ? 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                                : 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-200'
                            }`}
                            title={`PO line status: ${po.status_label}`}
                          >
                            {po.status_label}
                          </span>
                        )}
                        {enc != null && (
                          <span
                            className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${
                              encEmpty
                                ? 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                                : 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-300'
                            }`}
                            title="Open Encumbrance: amount still available to invoice against this PO line"
                          >
                            {encEmpty ? 'Fully invoiced' : `Open Encumbrance ${fmtMoney(enc)}`}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1.5 flex items-center gap-2 flex-wrap">
                      <span>Qty {fmtQty(po.quantity)} {po.unit_of_measure || ''}</span>
                      <span>· Unit {fmtMoney(po.unit_price)}</span>
                      <span>· Line {fmtMoney(po.total_price)}</span>
                    </div>

                    {(po.amount_paid_to_date != null || po.quantity_invoiced != null) && (
                      <div
                        className="text-[11px] text-gray-600 dark:text-gray-300 mt-1 flex items-center gap-2 flex-wrap"
                        title="How much has already been invoiced against this PO line to date. Pulled live from Keystone."
                      >
                        {po.amount_paid_to_date != null && (
                          <span>
                            Invoiced to date <span className="font-semibold">{fmtMoney(po.amount_paid_to_date)}</span>
                          </span>
                        )}
                        {po.amount_paid_to_date != null && po.quantity_invoiced != null && <span>·</span>}
                        {po.quantity_invoiced != null && (
                          <span>
                            {po.quantity_invoiced} prior invoice{po.quantity_invoiced === 1 ? '' : 's'}
                          </span>
                        )}
                      </div>
                    )}

                    {/* Show discount / freight / tax allocations from
                        the PO line so the clerk can
                        route the invoice's freight line to whichever PO line
                        the freight was attributed to. */}
                    {(Number(po.discount_amount) > 0 ||
                      Number(po.freight_amount) > 0 ||
                      Number(po.tax_amount) > 0) && (
                      <div className="mt-1 flex items-center gap-1.5 flex-wrap">
                        {Number(po.freight_amount) > 0 && (
                          <span
                            className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-200"
                            title="This PO line carries a freight allocation. If your invoice has freight, match its freight line here."
                          >
                            FRT {fmtMoney(Number(po.freight_amount))}
                          </span>
                        )}
                        {Number(po.discount_amount) > 0 && (
                          <span
                            className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200"
                            title="This PO line carries a discount allocation."
                          >
                            DISC {fmtMoney(Number(po.discount_amount))}
                          </span>
                        )}
                        {Number(po.tax_amount) > 0 && (
                          <span
                            className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-purple-100 dark:bg-purple-900/40 text-purple-800 dark:text-purple-200"
                            title="This PO line carries a tax allocation."
                          >
                            TAX {fmtMoney(Number(po.tax_amount))}
                          </span>
                        )}
                      </div>
                    )}

                    <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-1 flex items-center gap-2 flex-wrap font-mono">
                      <span>GL {po.gl_account_number || '—'}</span>
                      <span>· key {po.detail_key}</span>
                    </div>

                    {isMatched && (
                      <div className="mt-2 pt-2 border-t border-gray-100 dark:border-gray-700 flex items-center gap-1.5">
                        <CheckCircleIcon className={`h-4 w-4 flex-shrink-0 ${isReused ? 'text-blue-600 dark:text-blue-400' : 'text-green-600 dark:text-green-400'}`} />
                        <span className={`text-[11px] font-medium ${isReused ? 'text-blue-700 dark:text-blue-300' : 'text-green-700 dark:text-green-400'}`}>
                          {isReused
                            ? `Used by ${matchedTo.length} invoice lines (#${matchedTo.map((i) => i + 1).join(', #')})`
                            : `Matched to invoice #${matchedTo[0] + 1}`}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Export preview footer */}
      {poData && invoiceLines.length > 0 && (
        <div className="px-4 py-3 bg-gray-50 dark:bg-gray-700/50 border-t border-gray-200 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400">
          <div className="font-semibold uppercase tracking-wide text-[10px] mb-1">Export preview</div>
          {invoiceLines.map((line, idx) => (
            <div key={`preview-${idx}`} className="font-mono">
              inv line #{idx + 1} →{' '}
              {line.matched_po_detail_key ? (
                <span className="text-green-700 dark:text-green-400">PO detail_key {line.matched_po_detail_key}</span>
              ) : (
                <span className="text-red-600 dark:text-red-400">UNMATCHED (invoice will fail import)</span>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Done button — X in the header closes the same modal, but a
          labeled Done button is clearer for clerks. */}
      {onClose && (
        <div className="px-4 py-3 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="flex items-center gap-1.5 px-5 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold rounded-md shadow-sm"
          >
            <CheckCircleIcon className="h-4 w-4" />
            Done
          </button>
        </div>
      )}
    </div>
  );

  // Modal wrapper keeps the matcher independent of the invoice form's
  // layout height, so a form re-render will not collapse mid-interaction.
  //
  // Portal to document.body so `fixed inset-0` really is fixed to the
  // viewport, not to some transformed ancestor in WorkspacePage. A parent
  // with `transform`, `filter`, `perspective`, or `will-change: transform`
  // silently turns `position: fixed` into "fixed to that ancestor", which
  // is what caused the sticky "Edit Invoice Data" header to sit on top of
  // the modal instead of behind it.
  if (onClose) {
    return createPortal(
      <div
        className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
      >
        <div className="max-w-5xl w-full max-h-[90vh] overflow-y-auto">
          {content}
        </div>
      </div>,
      document.body,
    );
  }

  return content;
};

export default POLineMatcher;
